import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { astrologerDashboardService } from '../../services/api';

// Async thunks
export const fetchAstrologerSummary = createAsyncThunk(
  'astrologerDashboard/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await astrologerDashboardService.getSummary();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchAstrologerUpcomingConsultations = createAsyncThunk(
  'astrologerDashboard/fetchUpcoming',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await astrologerDashboardService.getUpcomingConsultations(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchAstrologerRecentMessages = createAsyncThunk(
  'astrologerDashboard/fetchMessages',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await astrologerDashboardService.getRecentMessages(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchAstrologerEarnings = createAsyncThunk(
  'astrologerDashboard/fetchEarnings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await astrologerDashboardService.getEarnings(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const astrologerDashboardSlice = createSlice({
  name: 'astrologerDashboard',
  initialState: {
    summary: null,
    upcomingConsultations: [],
    upcomingTotal: 0,
    recentMessages: [],
    messagesTotal: 0,
    earnings: null,
    loading: {
      summary: false,
      upcoming: false,
      messages: false,
      earnings: false
    },
    error: {
      summary: null,
      upcoming: null,
      messages: null,
      earnings: null
    }
  },
  reducers: {
    clearAstrologerDashboardErrors: (state) => {
      state.error = {
        summary: null,
        upcoming: null,
        messages: null,
        earnings: null
      };
    },
    resetAstrologerDashboard: (state) => {
      state.summary = null;
      state.upcomingConsultations = [];
      state.upcomingTotal = 0;
      state.recentMessages = [];
      state.messagesTotal = 0;
      state.earnings = null;
      state.error = {
        summary: null,
        upcoming: null,
        messages: null,
        earnings: null
      };
    }
  },
  extraReducers: (builder) => {
    // Dashboard Summary
    builder
      .addCase(fetchAstrologerSummary.pending, (state) => {
        state.loading.summary = true;
        state.error.summary = null;
      })
      .addCase(fetchAstrologerSummary.fulfilled, (state, action) => {
        state.loading.summary = false;
        state.summary = action.payload.data || action.payload;
      })
      .addCase(fetchAstrologerSummary.rejected, (state, action) => {
        state.loading.summary = false;
        state.error.summary = action.payload;
      });

    // Upcoming Consultations
    builder
      .addCase(fetchAstrologerUpcomingConsultations.pending, (state) => {
        state.loading.upcoming = true;
        state.error.upcoming = null;
      })
      .addCase(fetchAstrologerUpcomingConsultations.fulfilled, (state, action) => {
        state.loading.upcoming = false;
        const data = action.payload.data || action.payload;
        state.upcomingConsultations = data.consultations || [];
        state.upcomingTotal = data.total || 0;
      })
      .addCase(fetchAstrologerUpcomingConsultations.rejected, (state, action) => {
        state.loading.upcoming = false;
        state.error.upcoming = action.payload;
      });

    // Recent Messages
    builder
      .addCase(fetchAstrologerRecentMessages.pending, (state) => {
        state.loading.messages = true;
        state.error.messages = null;
      })
      .addCase(fetchAstrologerRecentMessages.fulfilled, (state, action) => {
        state.loading.messages = false;
        const data = action.payload.data || action.payload;
        state.recentMessages = data.messages || [];
        state.messagesTotal = data.total || 0;
      })
      .addCase(fetchAstrologerRecentMessages.rejected, (state, action) => {
        state.loading.messages = false;
        state.error.messages = action.payload;
      });

    // Earnings
    builder
      .addCase(fetchAstrologerEarnings.pending, (state) => {
        state.loading.earnings = true;
        state.error.earnings = null;
      })
      .addCase(fetchAstrologerEarnings.fulfilled, (state, action) => {
        state.loading.earnings = false;
        state.earnings = action.payload.data || action.payload;
      })
      .addCase(fetchAstrologerEarnings.rejected, (state, action) => {
        state.loading.earnings = false;
        state.error.earnings = action.payload;
      });
  }
});

export const { clearAstrologerDashboardErrors, resetAstrologerDashboard } = astrologerDashboardSlice.actions;

export default astrologerDashboardSlice.reducer;
